import { useState } from 'react';
import { ConfirmModal } from '../common/Modal';
import { Button } from '../common/Button';
import { useKillSwitch, useSetKillSwitch } from '../../hooks/useBotAdmin';

/**
 * Phase 75 (Plan 75-05) — org-wide kill-switch toggle (ADMIN-02 UI /
 * T-75-05-02). Flipping the switch is high-blast-radius (stops ALL Monk bot
 * activity for every user org-wide), so it is ALWAYS guarded by an explicit
 * confirm modal built on the shared `ConfirmModal` (Modal + Button) —
 * `window.confirm()` is never used anywhere in this component.
 */
export function KillSwitchToggle() {
  const { data, isLoading, error } = useKillSwitch();
  const setKillSwitchMutation = useSetKillSwitch();
  const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null);

  const enabled = data?.enabled ?? false;
  const isModalOpen = pendingEnabled !== null;

  const requestToggle = () => {
    setPendingEnabled(!enabled);
  };

  const closeModal = () => {
    if (setKillSwitchMutation.isPending) return;
    setPendingEnabled(null);
  };

  const handleConfirm = () => {
    if (pendingEnabled === null) return;
    setKillSwitchMutation.mutate(pendingEnabled, {
      onSuccess: () => setPendingEnabled(null),
    });
  };

  const turningOn = pendingEnabled === true;

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-gray-900">Org-wide kill switch</p>
          <p className="text-sm mt-0.5">
            {isLoading ? (
              <span className="text-gray-400">Loading...</span>
            ) : error ? (
              <span className="text-red-600">Failed to load kill-switch state.</span>
            ) : (
              <span className={enabled ? 'text-red-600 font-medium' : 'text-green-600 font-medium'}>
                {enabled ? 'ON — all Monk bot activity is blocked' : 'OFF — Monk bot is operating normally'}
              </span>
            )}
          </p>
        </div>
        <Button
          type="button"
          variant={enabled ? 'secondary' : 'danger'}
          onClick={requestToggle}
          disabled={isLoading || !!error}
        >
          {enabled ? 'Turn off' : 'Turn on'}
        </Button>
      </div>

      <ConfirmModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onConfirm={handleConfirm}
        title={turningOn ? 'Turn ON the kill switch?' : 'Turn OFF the kill switch?'}
        message={
          turningOn
            ? 'This stops ALL Monk bot activity for every user within ~2 minutes and blocks new connections. Existing conversations will fail until you turn the kill switch off again.'
            : 'This resumes Monk bot activity for every user and allows new connections. Any admin-disabled individual users stay disabled.'
        }
        confirmText={turningOn ? 'Turn on' : 'Turn off'}
        cancelText="Cancel"
        variant={turningOn ? 'danger' : 'primary'}
        isLoading={setKillSwitchMutation.isPending}
        error={setKillSwitchMutation.isError ? (setKillSwitchMutation.error as Error).message : null}
      />
    </div>
  );
}
